import {createApiCall, fetchAllPages} from '../utils';
import {SERVICE_NAMES} from '../constants';
import type {ApiCallOptions, PaginatedResponse} from '../types/api';
import type {
    RecipientResource,
    StoreRecipientRequest,
    UpdateRecipientRequest,
    UpdateRecipientLanguageRequest,
    ReorderRecipientsRequest,
    ChangeRecipientRequest,
    CloneRecipientRequest,
    CreateTwoFactorAuthenticationRequest,
    ValidateTwoFactorAuthenticationRequest,
    TwoFactorAuthenticationResource,
    SigningStatusResource,
    LowestSigningOrderResource,
    SearchRecipientsParams,
    ListRecipientsParams
} from './api.d';

/**
 * Recipient Service API
 * Provides functions for managing recipients in transactions.
 * All functions automatically handle authentication and environment detection.
 */
export const recipientApi = {
    /**
     * Search recipients in account
     * GET /accounts/{accountId}/search
     *
     * @param accountId - Account ID to search within
     * @param params - Search parameters (search query is required)
     * @param options - API call options (auth, timeout, headers)
     * @returns List of recipients matching search criteria
     */
    searchRecipients: (
        accountId: string,
        params: SearchRecipientsParams,
        options?: ApiCallOptions
    ) => createApiCall<RecipientResource[]>(
        SERVICE_NAMES.RECIPIENT,
        'GET',
        `accounts/${accountId}/search`
    )({...options, params}),

    /**
     * Get awaiting signatures
     * GET /awaiting-signatures
     *
     * @param options - API call options (auth, timeout, headers)
     * @returns List of recipients awaiting signatures
     */
    getAwaitingSignatures: (
        options?: ApiCallOptions
    ) => createApiCall<RecipientResource[]>(
        SERVICE_NAMES.RECIPIENT,
        'GET',
        'awaiting-signatures'
    )({...options}),

    /**
     * Get recipient by deprecated role ID
     * GET /transactions/{transaction}/deprecated-roles/{deprecatedRoleId}
     *
     * @param transactionId - Transaction ID
     * @param deprecatedRoleId - Legacy role ID (e.g., Signer1, Signer2)
     * @param options - API call options (auth, timeout, headers)
     * @returns Recipient matching the deprecated role ID
     */
    getRecipientByDeprecatedRole: (
        transactionId: string,
        deprecatedRoleId: string,
        options?: ApiCallOptions
    ) => createApiCall<RecipientResource>(
        SERVICE_NAMES.RECIPIENT,
        'GET',
        `transactions/${transactionId}/deprecated-roles/${deprecatedRoleId}`
    )({...options}),

    /**
     * Get recipient by deprecated signer ID
     * GET /transactions/{transaction}/deprecated-signers/{deprecatedSignerId}
     *
     * @param transactionId - Transaction ID
     * @param deprecatedSignerId - Legacy signer ID
     * @param options - API call options (auth, timeout, headers)
     * @returns Recipient matching the deprecated signer ID
     */
    getRecipientByDeprecatedSigner: (
        transactionId: string,
        deprecatedSignerId: string,
        options?: ApiCallOptions
    ) => createApiCall<RecipientResource>(
        SERVICE_NAMES.RECIPIENT,
        'GET',
        `transactions/${transactionId}/deprecated-signers/${deprecatedSignerId}`
    )({...options}),

    /**
     * Get signing status for transaction
     * GET /transactions/{transaction}/recipients/signing-status
     *
     * @param transactionId - Transaction ID
     * @param options - API call options (auth, timeout, headers)
     * @returns Signing status information
     */
    getSigningStatus: (
        transactionId: string,
        options?: ApiCallOptions
    ) => createApiCall<SigningStatusResource>(
        SERVICE_NAMES.RECIPIENT,
        'GET',
        `transactions/${transactionId}/recipients/signing-status`
    )({...options}),

    /**
     * Get lowest signing order for transaction
     * GET /transactions/{transaction}/recipients/lowest-signing-order
     *
     * @param transactionId - Transaction ID
     * @param options - API call options (auth, timeout, headers)
     * @returns Lowest signing order information
     */
    getLowestSigningOrder: (
        transactionId: string,
        options?: ApiCallOptions
    ) => createApiCall<LowestSigningOrderResource>(
        SERVICE_NAMES.RECIPIENT,
        'GET',
        `transactions/${transactionId}/recipients/lowest-signing-order`
    )({...options}),

    /**
     * List recipients for transaction with pagination and filtering
     * GET /transactions/{transactionId}/recipients
     *
     * @param transactionId - Transaction ID
     * @param params - List parameters (pagination, filtering by name/email/status/placeholders)
     * @param options - API call options (auth, timeout, headers)
     * @returns Paginated list of recipients
     */
    getRecipients: (
        transactionId: string,
        params?: ListRecipientsParams,
        options?: ApiCallOptions
    ) => createApiCall<PaginatedResponse<RecipientResource>>(
        SERVICE_NAMES.RECIPIENT,
        'GET',
        `transactions/${transactionId}/recipients`
    )({...options, params}),

    /**
     * Create new recipient in transaction
     * POST /transactions/{transaction}/recipients
     *
     * @param transactionId - Transaction ID
     * @param data - Recipient data
     * @param options - API call options (auth, timeout, headers)
     * @returns Created recipient
     */
    createRecipient: (
        transactionId: string,
        data: StoreRecipientRequest,
        options?: ApiCallOptions
    ) => createApiCall<RecipientResource>(
        SERVICE_NAMES.RECIPIENT,
        'POST',
        `transactions/${transactionId}/recipients`
    )({...options, data}),

    /**
     * Reorder recipients in transaction
     * PUT /transactions/{transaction}/recipients
     *
     * @param transactionId - Transaction ID
     * @param data - Reorder data with recipient IDs and new orders
     * @param options - API call options (auth, timeout, headers)
     * @returns Updated recipients
     */
    reorderRecipients: (
        transactionId: string,
        data: ReorderRecipientsRequest,
        options?: ApiCallOptions
    ) => createApiCall<RecipientResource[]>(
        SERVICE_NAMES.RECIPIENT,
        'PUT',
        `transactions/${transactionId}/recipients`
    )({...options, data}),

    /**
     * Get recipient details
     * GET /transactions/{transactionId}/recipients/{recipient}
     *
     * @param transactionId - Transaction ID
     * @param recipientId - Recipient ID
     * @param options - API call options (auth, timeout, headers)
     * @returns Recipient details
     */
    getRecipient: (
        transactionId: string,
        recipientId: string,
        options?: ApiCallOptions
    ) => createApiCall<RecipientResource>(
        SERVICE_NAMES.RECIPIENT,
        'GET',
        `transactions/${transactionId}/recipients/${recipientId}`
    )({...options}),

    /**
     * Update recipient
     * PUT /transactions/{transaction}/recipients/{recipient}
     *
     * @param transactionId - Transaction ID
     * @param recipientId - Recipient ID
     * @param data - Updated recipient data
     * @param options - API call options (auth, timeout, headers)
     * @returns Updated recipient
     */
    updateRecipient: (
        transactionId: string,
        recipientId: string,
        data: UpdateRecipientRequest,
        options?: ApiCallOptions
    ) => createApiCall<RecipientResource>(
        SERVICE_NAMES.RECIPIENT,
        'PUT',
        `transactions/${transactionId}/recipients/${recipientId}`
    )({...options, data}),

    /**
     * Delete recipient
     * DELETE /transactions/{transaction}/recipients/{recipient}
     *
     * @param transactionId - Transaction ID
     * @param recipientId - Recipient ID
     * @param options - API call options (auth, timeout, headers)
     * @returns void
     */
    deleteRecipient: (
        transactionId: string,
        recipientId: string,
        options?: ApiCallOptions
    ) => createApiCall<void>(
        SERVICE_NAMES.RECIPIENT,
        'DELETE',
        `transactions/${transactionId}/recipients/${recipientId}`
    )({...options}),

    /**
     * Lock recipient to prevent changes
     * PUT /transactions/{transaction}/recipients/{recipient}/lock
     *
     * @param transactionId - Transaction ID
     * @param recipientId - Recipient ID
     * @param options - API call options (auth, timeout, headers)
     * @returns Locked recipient
     */
    lockRecipient: (
        transactionId: string,
        recipientId: string,
        options?: ApiCallOptions
    ) => createApiCall<RecipientResource>(
        SERVICE_NAMES.RECIPIENT,
        'PUT',
        `transactions/${transactionId}/recipients/${recipientId}/lock`
    )({...options}),

    /**
     * Unlock recipient to allow changes
     * PUT /transactions/{transaction}/recipients/{recipient}/unlock
     *
     * @param transactionId - Transaction ID
     * @param recipientId - Recipient ID
     * @param options - API call options (auth, timeout, headers)
     * @returns Unlocked recipient
     */
    unlockRecipient: (
        transactionId: string,
        recipientId: string,
        options?: ApiCallOptions
    ) => createApiCall<RecipientResource>(
        SERVICE_NAMES.RECIPIENT,
        'PUT',
        `transactions/${transactionId}/recipients/${recipientId}/unlock`
    )({...options}),

    /**
     * Update recipient language
     * PATCH /transactions/{transaction}/recipients/{recipient}/language
     *
     * @param transactionId - Transaction ID
     * @param recipientId - Recipient ID
     * @param data - Language update data
     * @param options - API call options (auth, timeout, headers)
     * @returns Updated recipient
     */
    updateRecipientLanguage: (
        transactionId: string,
        recipientId: string,
        data: UpdateRecipientLanguageRequest,
        options?: ApiCallOptions
    ) => createApiCall<RecipientResource>(
        SERVICE_NAMES.RECIPIENT,
        'PATCH',
        `transactions/${transactionId}/recipients/${recipientId}/language`
    )({...options, data}),

    /**
     * Initialize signing for recipient
     * POST /transactions/{transaction}/recipients/{recipient}/initialize-signing
     *
     * @param transactionId - Transaction ID
     * @param recipientId - Recipient ID
     * @param options - API call options (auth, timeout, headers)
     * @returns Recipient with initialized signing
     */
    initializeSigning: (
        transactionId: string,
        recipientId: string,
        options?: ApiCallOptions
    ) => createApiCall<RecipientResource>(
        SERVICE_NAMES.RECIPIENT,
        'POST',
        `transactions/${transactionId}/recipients/${recipientId}/initialize-signing`
    )({...options}),

    /**
     * Change recipient details (email, name, etc.)
     * POST /transactions/{transaction}/recipients/{recipient}/change-recipient
     *
     * @param transactionId - Transaction ID
     * @param recipientId - Recipient ID
     * @param data - New recipient details
     * @param options - API call options (auth, timeout, headers)
     * @returns Updated recipient
     */
    changeRecipient: (
        transactionId: string,
        recipientId: string,
        data: ChangeRecipientRequest,
        options?: ApiCallOptions
    ) => createApiCall<RecipientResource>(
        SERVICE_NAMES.RECIPIENT,
        'POST',
        `transactions/${transactionId}/recipients/${recipientId}/change-recipient`
    )({...options, data}),

    /**
     * Clone recipients from template transaction
     * POST /transactions/{transactionId}/clone
     *
     * @param transactionId - Source transaction/template ID
     * @param data - Clone configuration with target transaction ID
     * @param options - API call options (auth, timeout, headers)
     * @returns Cloned recipients
     */
    cloneRecipients: (
        transactionId: string,
        data: CloneRecipientRequest,
        options?: ApiCallOptions
    ) => createApiCall<RecipientResource[]>(
        SERVICE_NAMES.RECIPIENT,
        'POST',
        `transactions/${transactionId}/clone`
    )({...options, data}),

    /**
     * Create two-factor authentication for recipient
     * POST /transactions/{transaction}/recipients/{recipient}/two-factor-authentications
     *
     * @param transactionId - Transaction ID
     * @param recipientId - Recipient ID
     * @param data - Two-factor authentication configuration
     * @param options - API call options (auth, timeout, headers)
     * @returns Created two-factor authentication
     */
    createTwoFactorAuthentication: (
        transactionId: string,
        recipientId: string,
        data: CreateTwoFactorAuthenticationRequest,
        options?: ApiCallOptions
    ) => createApiCall<TwoFactorAuthenticationResource>(
        SERVICE_NAMES.RECIPIENT,
        'POST',
        `transactions/${transactionId}/recipients/${recipientId}/two-factor-authentications`
    )({...options, data}),

    /**
     * Validate two-factor authentication code
     * POST /transactions/{transaction}/recipients/{recipient}/two-factor-authentications/{twoFactorAuthentication}/validate
     *
     * @param transactionId - Transaction ID
     * @param recipientId - Recipient ID
     * @param twoFactorAuthenticationId - Two-factor authentication ID
     * @param data - Validation data (secret code or answers)
     * @param options - API call options (auth, timeout, headers)
     * @returns void (204 No Content)
     */
    validateTwoFactorAuthentication: (
        transactionId: string,
        recipientId: string,
        twoFactorAuthenticationId: string,
        data: ValidateTwoFactorAuthenticationRequest,
        options?: ApiCallOptions
    ) => createApiCall<void>(
        SERVICE_NAMES.RECIPIENT,
        'POST',
        `transactions/${transactionId}/recipients/${recipientId}/two-factor-authentications/${twoFactorAuthenticationId}/validate`
    )({...options, data}),

    /**
     * Resend SMS for two-factor authentication
     * PUT /transactions/{transaction}/recipients/{recipient}/two-factor-authentications/{twoFactorAuthentication}/resend-sms
     *
     * @param transactionId - Transaction ID
     * @param recipientId - Recipient ID
     * @param twoFactorAuthenticationId - Two-factor authentication ID
     * @param options - API call options (auth, timeout, headers)
     * @returns void (204 No Content)
     */
    resendTwoFactorAuthenticationSms: (
        transactionId: string,
        recipientId: string,
        twoFactorAuthenticationId: string,
        options?: ApiCallOptions
    ) => createApiCall<void>(
        SERVICE_NAMES.RECIPIENT,
        'PUT',
        `transactions/${transactionId}/recipients/${recipientId}/two-factor-authentications/${twoFactorAuthenticationId}/resend-sms`
    )({...options}),

    /**
     * Get all recipients for transaction with automatic pagination
     * Fetches all pages automatically
     *
     * @param transactionId - Transaction ID
     * @param params - Filter parameters (without page)
     * @param options - API call options (auth, timeout, headers)
     * @returns All recipients
     */
    getAllRecipients: (
        transactionId: string,
        params?: Omit<ListRecipientsParams, 'page'>,
        options?: ApiCallOptions
    ) => fetchAllPages<RecipientResource, any>(
        (paginationParams, opts) =>
            recipientApi.getRecipients(transactionId, paginationParams, opts),
        params || {},
        options
    )
};
